import { Component, Show, createSignal, onMount } from "solid-js";
import { useSearchParams } from "@solidjs/router";
import { GitHubIcon } from "../components/landing/GitHubIcon";
import { logoSrc } from "../stores/themeStore";

const errorMessages: Record<string, string> = {
  oauth_failed: "GitHub authorization was denied or failed.",
  token_exchange_failed: "Failed to complete sign in with GitHub.",
  profile_fetch_failed: "Failed to retrieve your GitHub profile.",
  signup_incomplete:
    "We couldn't finish setting up your account. Please try signing in again.",
};

export const LoginPage: Component = () => {
  const [searchParams] = useSearchParams<{ error?: string }>();
  const [checking, setChecking] = createSignal(true);
  const [agreed, setAgreed] = createSignal(false);

  // If the user already has a valid session, redirect based on status
  onMount(async () => {
    try {
      const resp = await fetch("/api/auth/me");
      if (resp.ok) {
        const data = await resp.json();
        window.location.href = data.status === "active" ? "/board" : "/pending";
        return;
      }
    } catch {
      // Not authenticated — show login page
    }
    setChecking(false);
  });

  const errorMessage = () => {
    const err = searchParams.error;
    if (!err || typeof err !== "string") return null;
    return errorMessages[err] ?? "An unexpected error occurred.";
  };

  return (
    <Show when={!checking()}>
      <div class="min-h-screen bg-paper flex items-center justify-center p-4">
        <div class="w-full max-w-sm">
          {/* Logo and heading */}
          <div class="text-center mb-8">
            <img
              src={logoSrc()}
              alt="Flowicity"
              class="w-16 h-16 mx-auto mb-4 rounded-md"
            />
            <h1 class="text-2xl font-bold text-ink">Sign in to Flowicity</h1>
            <p class="mt-2 text-sm text-ink-2">AI-native project management</p>
          </div>

          {/* Error message */}
          <Show when={errorMessage()}>
            <div class="mb-4 p-3 bg-signal-red-tint border border-signal-red/25 rounded text-sm text-signal-red">
              {errorMessage()}
            </div>
          </Show>

          {/* Sign in card */}
          <div class="bg-surface rounded-md border border-line p-6">
            <button
              type="button"
              disabled={!agreed()}
              aria-describedby="legal-consent-label"
              onClick={() => {
                window.location.href = "/api/auth/github";
              }}
              class="flex items-center justify-center gap-3 w-full px-4 py-3 bg-ink text-paper rounded focus-ring hover:bg-ink-2 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <GitHubIcon class="w-5 h-5" />
              Sign in with GitHub
            </button>

            <div class="mt-4 flex items-start gap-2">
              <input
                type="checkbox"
                id="legal-consent"
                checked={agreed()}
                onChange={(e) => setAgreed(e.currentTarget.checked)}
                class="mt-0.5 focus-ring"
              />
              <label
                for="legal-consent"
                id="legal-consent-label"
                class="text-xs text-ink-3"
              >
                I agree to the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-ink-2 hover:text-ink underline underline-offset-2"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-ink-2 hover:text-ink underline underline-offset-2"
                >
                  Privacy Policy
                </a>
              </label>
            </div>
          </div>
        </div>
      </div>
    </Show>
  );
};
